import React, { useState, useEffect } from 'react';
import { X, Upload, Plus, Trash2 } from 'lucide-react';
import { toast } from 'sonner';

const EditVehicleModal = ({ isOpen, onClose, onSuccess, vehicle }) => {
    const [formData, setFormData] = useState({
        make: '',
        model: '',
        year: new Date().getFullYear(),
        category: 'Sedan',
        daily_rate: '',
        features: [],
        city: '',
        status: 'available'
    });

    const [newFeature, setNewFeature] = useState('');
    const [loading, setLoading] = useState(false);

    const categories = ['Sedan', 'SUV', 'Compact', 'Luxury', 'Van', 'Truck'];
    const statuses = [
        { value: 'available', label: 'Disponible', color: 'green' },
        { value: 'rented', label: 'Loué', color: 'blue' },
        { value: 'maintenance', label: 'Maintenance', color: 'yellow' }
    ];
    const commonFeatures = ['GPS', 'Climatisation', 'Bluetooth', 'USB', 'Caméra de recul', 'Sièges chauffants', 'Toit ouvrant'];

    useEffect(() => {
        if (vehicle) {
            setFormData({
                make: vehicle.make || '',
                model: vehicle.model || '',
                year: vehicle.year || new Date().getFullYear(),
                category: vehicle.category || 'Sedan',
                daily_rate: vehicle.daily_rate || '',
                features: vehicle.features || [],
                city: vehicle.city || '',
                status: vehicle.status || 'available'
            });
        }
    }, [vehicle]);

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!formData.make || !formData.model || !formData.daily_rate || !formData.city) {
            toast.error('Veuillez remplir tous les champs obligatoires');
            return;
        }

        setLoading(true);

        try {
            const token = localStorage.getItem('token');
            const response = await fetch(`http://localhost:8001/api/agencies/my/vehicles/${vehicle.id}`, {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify({
                    ...formData,
                    daily_rate: parseInt(formData.daily_rate)
                })
            });

            if (!response.ok) {
                throw new Error('Erreur lors de la mise à jour du véhicule');
            }

            toast.success('Véhicule mis à jour avec succès!');
            onSuccess();
            onClose();
        } catch (error) {
            console.error('Error updating vehicle:', error);
            toast.error('Erreur lors de la mise à jour du véhicule');
        } finally {
            setLoading(false);
        }
    };

    const addFeature = () => {
        if (newFeature && !formData.features.includes(newFeature)) {
            setFormData({
                ...formData,
                features: [...formData.features, newFeature]
            });
            setNewFeature('');
        }
    };

    const removeFeature = (feature) => {
        setFormData({
            ...formData,
            features: formData.features.filter(f => f !== feature)
        });
    };

    const addCommonFeature = (feature) => {
        if (!formData.features.includes(feature)) {
            setFormData({
                ...formData,
                features: [...formData.features, feature]
            });
        }
    };

    if (!isOpen || !vehicle) return null;

    return (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
            <div className="bg-gradient-to-br from-gray-900 to-gray-800 rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto border border-gray-700">
                {/* Header */}
                <div className="sticky top-0 bg-gradient-to-r from-purple-600 to-pink-600 p-6 flex justify-between items-center rounded-t-2xl">
                    <h2 className="text-2xl font-bold text-white">Modifier le véhicule</h2>
                    <button
                        onClick={onClose}
                        className="text-white/80 hover:text-white transition-colors"
                    >
                        <X className="w-6 h-6" />
                    </button>
                </div>

                {/* Form */}
                <form onSubmit={handleSubmit} className="p-6 space-y-6">
                    {/* Basic Info */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                            <label className="block text-sm font-medium text-gray-300 mb-2">
                                Marque *
                            </label>
                            <input
                                type="text"
                                value={formData.make}
                                onChange={(e) => setFormData({ ...formData, make: e.target.value })}
                                className="w-full px-4 py-3 bg-gray-800/50 border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-purple-500"
                                required
                            />
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-300 mb-2">
                                Modèle *
                            </label>
                            <input
                                type="text"
                                value={formData.model}
                                onChange={(e) => setFormData({ ...formData, model: e.target.value })}
                                className="w-full px-4 py-3 bg-gray-800/50 border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-purple-500"
                                required
                            />
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-300 mb-2">
                                Année
                            </label>
                            <input
                                type="number"
                                value={formData.year}
                                onChange={(e) => setFormData({ ...formData, year: parseInt(e.target.value) })}
                                className="w-full px-4 py-3 bg-gray-800/50 border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-purple-500"
                                min="2000"
                                max={new Date().getFullYear() + 1}
                            />
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-300 mb-2">
                                Catégorie
                            </label>
                            <select
                                value={formData.category}
                                onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                                className="w-full px-4 py-3 bg-gray-800/50 border border-gray-700 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-purple-500"
                            >
                                {categories.map(cat => (
                                    <option key={cat} value={cat}>{cat}</option>
                                ))}
                            </select>
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-300 mb-2">
                                Prix/Jour (DZD) *
                            </label>
                            <input
                                type="number"
                                value={formData.daily_rate}
                                onChange={(e) => setFormData({ ...formData, daily_rate: e.target.value })}
                                className="w-full px-4 py-3 bg-gray-800/50 border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-purple-500"
                                min="0"
                                required
                            />
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-300 mb-2">
                                Ville *
                            </label>
                            <input
                                type="text"
                                value={formData.city}
                                onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                                className="w-full px-4 py-3 bg-gray-800/50 border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-purple-500"
                                required
                            />
                        </div>
                    </div>

                    {/* Status */}
                    <div>
                        <label className="block text-sm font-medium text-gray-300 mb-2">
                            Statut
                        </label>
                        <div className="grid grid-cols-3 gap-3">
                            {statuses.map(status => (
                                <button
                                    key={status.value}
                                    type="button"
                                    onClick={() => setFormData({ ...formData, status: status.value })}
                                    className={`px-4 py-3 rounded-lg border-2 transition-all ${formData.status === status.value
                                            ? `border-${status.color}-500 bg-${status.color}-500/20 text-${status.color}-300`
                                            : 'border-gray-700 bg-gray-800/50 text-gray-400 hover:border-gray-600'
                                        }`}
                                >
                                    {status.label}
                                </button>
                            ))}
                        </div>
                    </div>

                    {/* Features */}
                    <div>
                        <label className="block text-sm font-medium text-gray-300 mb-2">
                            Équipements
                        </label>

                        {/* Common Features */}
                        <div className="flex flex-wrap gap-2 mb-3">
                            {commonFeatures.map(feature => (
                                <button
                                    key={feature}
                                    type="button"
                                    onClick={() => addCommonFeature(feature)}
                                    disabled={formData.features.includes(feature)}
                                    className={`px-3 py-1 rounded-full text-sm transition-all ${formData.features.includes(feature)
                                            ? 'bg-purple-600 text-white'
                                            : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                                        }`}
                                >
                                    {feature}
                                </button>
                            ))}
                        </div>

                        {/* Custom Feature Input */}
                        <div className="flex gap-2">
                            <input
                                type="text"
                                value={newFeature}
                                onChange={(e) => setNewFeature(e.target.value)}
                                onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addFeature())}
                                className="flex-1 px-4 py-2 bg-gray-800/50 border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-purple-500"
                                placeholder="Ajouter un équipement personnalisé..."
                            />
                            <button
                                type="button"
                                onClick={addFeature}
                                className="px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg transition-colors flex items-center gap-2"
                            >
                                <Plus className="w-4 h-4" />
                                Ajouter
                            </button>
                        </div>

                        {/* Selected Features */}
                        {formData.features.length > 0 && (
                            <div className="mt-3 flex flex-wrap gap-2">
                                {formData.features.map(feature => (
                                    <div
                                        key={feature}
                                        className="px-3 py-1 bg-purple-600/20 border border-purple-500/30 rounded-full text-purple-300 text-sm flex items-center gap-2"
                                    >
                                        {feature}
                                        <button
                                            type="button"
                                            onClick={() => removeFeature(feature)}
                                            className="hover:text-red-400 transition-colors"
                                        >
                                            <Trash2 className="w-3 h-3" />
                                        </button>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>

                    {/* Actions */}
                    <div className="flex gap-3 pt-4">
                        <button
                            type="button"
                            onClick={onClose}
                            className="flex-1 px-6 py-3 bg-gray-700 hover:bg-gray-600 text-white rounded-lg transition-colors font-medium"
                        >
                            Annuler
                        </button>
                        <button
                            type="submit"
                            disabled={loading}
                            className="flex-1 px-6 py-3 bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white rounded-lg transition-all font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            {loading ? 'Mise à jour...' : 'Mettre à jour'}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default EditVehicleModal;
